import { create } from 'zustand';
import type { MarisaAuto } from '~/@types/MarisaAuto/form';
import { getStorageLocationsUseCase } from '~/useCases/AssegurouUseCases/GetStorageLocationsUseCase';
import { getVehicleModelDetailsUseCase } from '~/useCases/AssegurouUseCases/GetVehicleModelDetailsUseCase';
import { getYearsByVehicleModelUseCase } from '~/useCases/AssegurouUseCases/GetYearsByVehicleModelUseCase';
import { Gender } from '~/utils/form';

const INITIAL_STATE_FORM: MarisaAuto.Form = <MarisaAuto.Form>{
  brandDescription: '',
  licensePlate: '',
  vehicle: {
    brand: '',
    year: 0,
    modelYear: 0,
    model: '',
    version: '',
    modelDescription: '',
  },
  fipeDetails: {
    fipeCode: '',
    fipeValue: '',
  },
  usageVehicle: '',
  storageVehicle: {},
  address: {
    zipcode: '',
    street: '',
    neighborhood: '',
    city: '',
    state: '',
  },
  document: '',
  services: {
    theft: '',
    accident: '',
    thridPartyAccident: '',
    fire: '',
    flooding: '',
    backupCar: '',
  },
  personalInformation: {
    fullName: '',
    birthDate: '',
    maritalStatus: '',
    gender: Gender.NOT_INFORMED,
  },
  phoneNumber: '',
  policyExpiration: '',
};

const INITIAL_STATE_SELECTED_INSURANCE_QUOTE: MarisaAuto.InsuranceQuote = {
  seguradora: '',
  numeroCotacao: '',
  codigoOferta: '',
  empresaCodigo: '',
  empresaDescricao: '',
  empresaRefLogo: '',
  valorFipe: '',
  fatorFipe: 0,
  valorFranquia: '',
  validadeOferta: '',
  nome: '',
  descricao: '',
  valorPremioFinal: '',
  coberturasCompleta: [],
  coberturas: [],
  formaPagamento: [],
  bestPaymentOption: {
    description: '',
    installments: 0,
    installmentValue: 0,
    totalPrice: 0,
    type: '',
  },
};

type State = {
  form: MarisaAuto.Form | null;
  insuranceQuotes: MarisaAuto.InsuranceQuote[];
  selectedInsuranceQuote: MarisaAuto.InsuranceQuote;
  setLicensePlate: (licensePlate: string) => void;
  setBrandDescription: (brandDescription: string) => void;
  setVehicle: (vehicle: MarisaAuto.Vehicle) => Promise<void>;
  setExpiryDate: (policyExpiration: string) => void;
  setUsageQuizAnswer: (answer: number) => void;
  setStorageQuizAnswer: (answer: {
    question: string;
    response: string;
  }) => void;
  loadStorageQuizQuestions: () => Promise<void>;
  setAddress: (address: MarisaAuto.Address) => void;
  setDocument: (document: string) => void;
  setServices: (services: MarisaAuto.Services) => void;
  setPersonalInformation: (services: MarisaAuto.PersonalInformation) => void;
  setPhoneNumber: (phoneNumber: string) => void;
  setInsuranceQuotes: (insuranceQuotes: MarisaAuto.InsuranceQuote[]) => void;
  setSelectedInsuranceQuote: (
    selectedInsuranceQuote: MarisaAuto.InsuranceQuote
  ) => void;
};

const updateForm = (
  state: State,
  field: keyof MarisaAuto.Form,
  value: any
): State => {
  return {
    ...state,
    form: { ...(state.form || { ...INITIAL_STATE_FORM }), [field]: value },
  };
};

const useMarisaAutoForm = create<State>((set, get) => ({
  form: { ...INITIAL_STATE_FORM },
  insuranceQuotes: [],
  selectedInsuranceQuote: INITIAL_STATE_SELECTED_INSURANCE_QUOTE,
  setLicensePlate: (licensePlate) =>
    set((state) => updateForm(state, 'licensePlate', licensePlate)),
  setBrandDescription: (brandDescription) =>
    set((state) => updateForm(state, 'brandDescription', brandDescription)),
  setVehicle: async (vehicle) => {
    const result = await getYearsByVehicleModelUseCase(
      vehicle.brand,
      vehicle.model
    );
    const yearVehicleModelCode = result.yearVehicleModels.find(
      (yearVehicleModel) => {
        if (yearVehicleModel && yearVehicleModel.codigo) {
          return yearVehicleModel.codigo.includes(vehicle.modelYear.toString());
        }
        return '';
      }
    )?.codigo;
    if (!yearVehicleModelCode) {
      throw new Error('Não foi possível encontrar o veículo');
    }
    const { success, fipeCode, fipeValue, modelDescription, brandDescription } =
      await getVehicleModelDetailsUseCase(
        vehicle.brand,
        vehicle.model,
        yearVehicleModelCode
      );
    if (!success) {
      throw new Error('Não foi possível encontrar o veículo');
    }
    set((state) =>
      updateForm(state, 'vehicle', {
        ...vehicle,
        modelDescription,
        brandDescription,
      })
    );
    set((state) => updateForm(state, 'fipeDetails', { fipeCode, fipeValue }));
  },
  setExpiryDate: (policyExpiration) =>
    set((state) => updateForm(state, 'policyExpiration', policyExpiration)),
  setUsageQuizAnswer: (value) =>
    set((state) => updateForm(state, 'usageVehicle', value)),
  loadStorageQuizQuestions: async () => {
    const storageVehicle = get().form?.storageVehicle;

    if (!storageVehicle || Object.keys(storageVehicle).length !== 0) {
      return;
    }

    const result = await getStorageLocationsUseCase();

    if (result.success) {
      set((state) =>
        updateForm(state, 'storageVehicle', result.storageLocations)
      );
    }
  },
  setStorageQuizAnswer: ({ question, response }) =>
    set((state) => {
      const currentQuestion = state.form?.storageVehicle[question];
      if (!currentQuestion) return state;
      return {
        ...state,
        form: {
          ...(state.form || { ...INITIAL_STATE_FORM }),
          storageVehicle: {
            ...(state.form?.storageVehicle || {}),
            [question]: {
              ...currentQuestion,
              selectedAnswer: Number(response),
            },
          },
        },
      };
    }),
  setAddress: (address) =>
    set((state) => updateForm(state, 'address', address)),
  setDocument: (document) =>
    set((state) => updateForm(state, 'document', document)),
  setServices: (services) =>
    set((state) => updateForm(state, 'services', services)),
  setPersonalInformation: (personalInformation) =>
    set((state) =>
      updateForm(state, 'personalInformation', personalInformation)
    ),
  setPhoneNumber: (phoneNumber) =>
    set((state) => updateForm(state, 'phoneNumber', phoneNumber)),
  setInsuranceQuotes: (insuranceQuotes) =>
    set((state) => ({ ...state, insuranceQuotes })),
  setSelectedInsuranceQuote: (selectedInsuranceQuote) =>
    set((state) => ({ ...state, selectedInsuranceQuote })),
}));

export default useMarisaAutoForm;
